import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Dialog, Disclosure, Tab, Transition } from "@headlessui/react";
import { useAuth } from "../utils/auth";
import { Font, Document, Page, StyleSheet, View, Text, Image, pdf } from "@react-pdf/renderer";
import { constants } from "../utils/ranges";
import { ReactComponent as BackArrow } from "../assets/icons/BackArrow.svg";
import { ReactComponent as Chevron } from "../assets/icons/Chevron.svg";
import { ReactComponent as Info } from "../assets/icons/Info.svg";
import { ReactComponent as Caret } from "../assets/icons/Caret.svg";
import BloodPressureScore from "../assets/images/scores/BloodPressure.png";
import HeartHealthScore from "../assets/images/scores/HeartHealth.png";
import MentalWellbeingScore from "../assets/images/scores/MentalWellbeing.png";
import PhysiologicalScore from "../assets/images/scores/Physiological.png";
import CompanyLogo from "../assets/images/IndusindLogo.png";
import CareplixLogo from "../assets/images/CarePlixLogo.png";
import Pointer from "../assets/images/report/Pointer.png";
import HeartHealth from "../assets/images/report/heart-health-score.png";
import MentalWellbeing from "../assets/images/report/mental-wellbeing-score.png";
import Physiological from "../assets/images/report/physiological-score.png";
import BloodPressure from "../assets/images/report/blood-pressure.png";
import HeartRate from "../assets/images/report/heart-rate.png";
import RespirationRate from "../assets/images/report/respiration-rate.png";
import OxygenSaturation from "../assets/images/report/oxygen-saturation.png";
import BMI from "../assets/images/report/bmi.png";
import HRV from "../assets/images/report/hrv.png";
import HRRelated from "../assets/images/report/hr-related.png";
import CardiacOutput from "../assets/images/report/cardiac-output.png";
import MAP from "../assets/images/report/map.png";
import HeartUtilization from "../assets/images/report/heart-utilization.png";
import StressIndex from "../assets/images/report/stress-index.png";
import VO2MAX from "../assets/images/report/vo2max.png";
import {
  getScanDuration,
  isMetricLocked,
  isMetricValueAvailable,
  SHORT_SCAN_LOCK_MESSAGE,
} from "../utils/scanDuration";

Font.register({
  family: "Poppins",
  fonts: [
    { src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLDz8V1tvFP-KUEg.ttf", fontWeight: "light" },
    { src: "https://fonts.gstatic.com/s/poppins/v20/pxiEyp8kv8JHgFVrFJDUc1NECPY.ttf", fontWeight: "normal" },
    { src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLGT9V1tvFP-KUEg.ttf", fontWeight: "medium" },
    { src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLEj6V1tvFP-KUEg.ttf", fontWeight: "semibold" },
    { src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLCz7V1tvFP-KUEg.ttf", fontWeight: "bold" },
  ],
});
Font.registerHyphenationCallback((word) => [word]);

const PDFReport = ({
  referralCode = "",
  personName = "",
  agentName = "",
  relation = "",
  age,
  gender = "",
  height,
  weight,
  scanDuration = 60,
  result = {},
}) => {
  const displayValue = (value, unit = "", metric = "") => {
    if (isMetricLocked(metric, scanDuration)) return SHORT_SCAN_LOCK_MESSAGE;
    return isMetricValueAvailable(value) ? `${value}${unit}` : "Unavailable";
  };
  const styles = StyleSheet.create({
    page: { backgroundColor: "#fff", flexDirection: "column", alignItems: "stretch", justifyContent: "space-between" },
    header: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: 40,
      paddingVertical: 16,
      borderBottomWidth: 3,
      borderBottomColor: "#ED1C24",
    },
    contentContainer: { flex: 1, paddingHorizontal: 40 },
    title: { fontFamily: "Poppins", fontWeight: "bold", fontSize: 14, color: "#1845a3", marginBottom: 16 },
    horizontalLine: { marginVertical: 8, height: 1, backgroundColor: "#D5D5D5", borderRadius: 999 },
    scoreTitle: { flex: 1, fontFamily: "Poppins", fontSize: 12 },
    trContainer: { flexDirection: "row", gap: 4, paddingVertical: 6, paddingHorizontal: 12 },
    trText: { flex: 1, fontSize: 10, fontFamily: "Poppins" },
    pTitle: { fontFamily: "Poppins", fontWeight: "semibold", fontSize: 12, color: "#1845a3", marginBottom: 4 },
    pDesc: { fontFamily: "Poppins", fontSize: 10, lineHeight: 1.5, textAlign: "justify" },
    pRange: { fontFamily: "Poppins", fontSize: 10, lineHeight: 1.25, textAlign: "justify" },
    gradientBar: { height: 4, backgroundColor: "#ED1C24" },
    footer: {
      paddingVertical: 20,
      paddingHorizontal: 40,
      backgroundColor: "#1845a3",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
    },
    footerText: { fontFamily: "Poppins", fontWeight: "medium", fontSize: 8, color: "#fff" },
  });

  const Disclaimer = ({ containerStyle = {} }) => (
    <View style={[{ marginTop: 36 }, containerStyle]}>
      <Text style={{ fontFamily: "Poppins", fontWeight: "semibold", fontSize: 10 }}>Disclaimer</Text>
      <Text style={{ fontFamily: "Poppins", fontSize: 7, lineHeight: 1.25, textAlign: "justify" }}>
        For Investigational Use Only. Careplix is not a substitute for the clinical judgment of a health care professional.
        Careplix is intended to improve your awareness of general wellness. Careplix does not diagnose, treat, mitigate or
        prevent any disease, symp-tom, disorder or abnormal physical state. Consult with a health care professional or emergency
        services if you believe you may have a medical issue.
      </Text>
    </View>
  );

  const Range = ({ ranges = [], selectedIndex = -1, containerStyle = {} }) => (
    <View style={[{ flexDirection: "row" }, containerStyle]}>
      {ranges.map((r, index, list) => (
        <View key={index} style={{ flex: 1, alignItems: "center" }}>
          <Text
            style={{
              color: r.color,
              fontFamily: "Poppins",
              fontSize: 8,
              marginBottom: 6,
              maxLines: 1,
              textOverflow: "ellipsis",
              textTransform: "uppercase",
            }}
          >
            {r.label}
          </Text>
          <View
            style={{
              alignSelf: "stretch",
              height: 6,
              backgroundColor: r.color,
              borderTopLeftRadius: index > 0 ? 0 : 20,
              borderBottomLeftRadius: index > 0 ? 0 : 20,
              borderTopRightRadius: index < list.length - 1 ? 0 : 20,
              borderBottomRightRadius: index < list.length - 1 ? 0 : 20,
            }}
          />
          {selectedIndex === index && <Image source={Pointer} style={{ height: 8, marginTop: 4 }} />}
        </View>
      ))}
    </View>
  );

  const Score = ({ icon, title = "", value }) => {
    const locked = isMetricLocked(title, scanDuration);
    const available = isMetricValueAvailable(value) && !locked;
    if (!available) {
      return (
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <Image source={icon} style={{ height: 20, width: 20, marginRight: 10 }} />
          <Text style={styles.scoreTitle}>{title}</Text>
          <Text style={[styles.scoreTitle, { color: "#6b7280" }]}>
            {locked ? SHORT_SCAN_LOCK_MESSAGE : "Unavailable"}
          </Text>
        </View>
      );
    }
    const rangeIndex = value >= 75 ? 0 : value >= 50 ? 1 : 2;
    const selectedRange = constants["Scores"].ranges[rangeIndex];
    return (
      <View style={{ flexDirection: "row", alignItems: "center" }}>
        <Image source={icon} style={{ height: 20, width: 20, marginRight: 10 }} />
        <Text style={styles.scoreTitle}>{title}</Text>
        <Text style={[styles.scoreTitle, { color: selectedRange.color, fontWeight: "semibold", textTransform: "uppercase" }]}>
          {selectedRange.label} Risk
        </Text>
        <Range containerStyle={{ flexBasis: "45%" }} ranges={constants["Scores"].ranges} selectedIndex={rangeIndex} />
      </View>
    );
  };

  const Table = ({ containerStyle = {}, title = "", data = [] }) => (
    <View style={containerStyle}>
      <View style={[styles.trContainer, { borderTopLeftRadius: 5, borderTopRightRadius: 5, backgroundColor: "#1845a3" }]}>
        <Text style={[styles.trText, { fontWeight: "semibold", color: "#fff" }]}>{title}</Text>
        <Text style={[styles.trText, { fontWeight: "semibold", color: "#fff" }]}>Value</Text>
        <Text style={[styles.trText, { fontWeight: "semibold", color: "#fff" }]}>Measurement Scale</Text>
        <Text style={[styles.trText, { fontWeight: "semibold", color: "#fff" }]}>Range</Text>
      </View>
      {data?.map?.(([title, value, scale, range], index) => (
        <View key={index} style={[styles.trContainer, { backgroundColor: Boolean(index % 2) ? "#f4f5f9" : "transparent" }]}>
          <Text style={styles.trText}>{title}</Text>
          <Text style={styles.trText}>{value}</Text>
          <Text style={[styles.trText, { color: scale?.color ?? "#000" }]}>{scale?.label}</Text>
          <Text style={styles.trText}>{range}</Text>
        </View>
      ))}
    </View>
  );

  const ParameterInfo = ({ containerStyle = {}, icon, title = "", desc = "", ranges = [] }) => (
    <View style={[{ flexDirection: "row", marginBottom: ranges.length > 0 ? 16 : 24 }, containerStyle]}>
      <Image source={icon} style={{ height: 30, width: 30, marginRight: 10 }} />
      <View style={{ flex: 1 }}>
        <Text style={styles.pTitle}>{title}</Text>
        <Text style={styles.pDesc}>{desc}</Text>
        {ranges.map((r, idx) => (
          <View key={`range-${idx}`} style={{ marginTop: 4, flexDirection: "row" }}>
            <Text style={[styles.pRange, { marginHorizontal: 4 }]}>•</Text>
            <Text style={[styles.pRange, { flex: 1 }]}>
              <Text style={{ fontWeight: "semibold" }}>{r.label}: </Text>
              <Text>{r.desc}</Text>
            </Text>
          </View>
        ))}
      </View>
    </View>
  );

  const bpRiskSelIdx = useMemo(
    () =>
      isMetricValueAvailable(result.bp_sys)
        ? result.bp_sys < 108 || result.bp_sys > 135
          ? 2
          : result.bp_sys < 115 || result.bp_sys > 125
          ? 1
          : 0
        : undefined,
    [result.bp_sys]
  );

  return (
    <Document title="CarePlix Report" subject="Report generated from CarePlix Vitals Scan" author="CarePlix">
      <Page wrap={false} style={{ backgroundColor: "#f8faff" }}>
        <View style={{ height: "100%", width: "100%", flexDirection: "column", alignItems: "center", justifyContent: "space-between", paddingVertical: 48, paddingHorizontal: 40 }}>
          <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 16, width: "100%" }}>
            <Image source={CompanyLogo} style={{ height: 48 }} />
            <View style={{ width: 1, height: 36, backgroundColor: "#d1d5db" }} />
            <View>
              <Text style={{ fontFamily: "Poppins", fontSize: 8, color: "#6b7280" }}>Powered by</Text>
              <Image source={CareplixLogo} style={{ height: 20 }} />
            </View>
          </View>
          <View style={{ alignItems: "center", paddingHorizontal: 20 }}>
            <View style={{ width: 80, height: 4, borderRadius: 2, backgroundColor: "#1845a3", marginBottom: 24 }} />
            <Text style={{ fontFamily: "Poppins", fontWeight: "bold", fontSize: 32, color: "#1845a3", textAlign: "center", lineHeight: 1.3 }}>
              Digital Health
            </Text>
            <Text style={{ fontFamily: "Poppins", fontWeight: "bold", fontSize: 32, color: "#ED1C24", textAlign: "center", lineHeight: 1.3 }}>
              Assessment Report
            </Text>
            <View style={{ width: 80, height: 4, borderRadius: 2, backgroundColor: "#ED1C24", marginTop: 24 }} />
            <Text style={{ fontFamily: "Poppins", fontSize: 11, color: "#6b7280", marginTop: 20, textAlign: "center" }}>
              A comprehensive overview of your health vitals and wellness parameters
            </Text>
          </View>
          <View style={{ width: "100%", paddingVertical: 20, paddingHorizontal: 24, backgroundColor: "#ffffff", borderRadius: 12, borderWidth: 1, borderColor: "#e5e7eb" }}>
            <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 12 }}>
              {personName ? (
                <View>
                  <Text style={{ fontFamily: "Poppins", fontSize: 8, color: "#9ca3af" }}>Customer Name</Text>
                  <Text style={{ fontFamily: "Poppins", fontWeight: "semibold", fontSize: 14, color: "#1845a3" }}>{personName}</Text>
                </View>
              ) : null}
              {agentName ? (
                <View style={{ alignItems: "flex-end" }}>
                  <Text style={{ fontFamily: "Poppins", fontSize: 8, color: "#9ca3af" }}>Agent Name</Text>
                  <Text style={{ fontFamily: "Poppins", fontWeight: "semibold", fontSize: 14, color: "#ED1C24" }}>{agentName}</Text>
                </View>
              ) : null}
            </View>
            <View style={{ height: 1, backgroundColor: "#e5e7eb", marginBottom: 12 }} />
            <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
              <View>
                <Text style={{ fontFamily: "Poppins", fontSize: 8, color: "#9ca3af" }}>Assessment Date</Text>
                <Text style={{ fontFamily: "Poppins", fontWeight: "medium", fontSize: 11, color: "#374151" }}>
                  {result.timestamp.toLocaleDateString(undefined, { day: "2-digit", month: "long", year: "numeric", timeZone: "Asia/Kolkata" })}
                </Text>
              </View>
              <View style={{ alignItems: "center" }}>
                <Text style={{ fontFamily: "Poppins", fontSize: 8, color: "#9ca3af" }}>Assessment Time</Text>
                <Text style={{ fontFamily: "Poppins", fontWeight: "medium", fontSize: 11, color: "#374151" }}>
                  {result.timestamp.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" })}
                </Text>
              </View>
            </View>
          </View>
          <Text style={{ fontFamily: "Poppins", fontSize: 7, color: "#9ca3af", textAlign: "center" }}>
            IndusInd General Insurance Company Limited | Powered by CarePlix
          </Text>
        </View>
      </Page>
      <Page style={styles.page}>
        <View fixed style={styles.header}>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
            <Image source={CompanyLogo} style={{ height: 40 }} />
            <View style={{ width: 1, height: 28, backgroundColor: "#d1d5db" }} />
            <View>
              <Text style={{ fontFamily: "Poppins", fontSize: 6, color: "#6b7280" }}>Powered by</Text>
              <Image source={CareplixLogo} style={{ height: 14 }} />
            </View>
          </View>
          <View style={{ alignItems: "flex-end" }}>
            {[
              {
                label: "Date",
                value: result.timestamp.toLocaleDateString(undefined, { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Asia/Kolkata" }),
              },
              {
                label: "Time",
                value: result.timestamp.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" }),
              },
            ].map(({ label, value }) => (
              <Text key={label.toLowerCase().replace(/\s+/g, "-")} style={{ fontFamily: "Poppins", fontSize: 10 }}>
                <Text style={{ fontWeight: "semibold", color: "#6b7280" }}>{label}: </Text>
                <Text style={{ color: "#374151" }}>{value}</Text>
              </Text>
            ))}
          </View>
        </View>
        <View style={styles.contentContainer}>
          <View style={{ flexDirection: "row", gap: 48 }}>
            <View style={{ flex: 1 }}>
              {[
                { label: "Relation", value: relation },
                { label: "Age", value: displayValue(age, " years") },
                { label: "Gender", value: gender },
                { label: "Height", value: displayValue(height, " cms") },
                { label: "Weight", value: displayValue(weight, " kgs") },
              ].map(({ label, value }, index) => (
                <Text
                  key={label.toLowerCase().replaceAll(" ", "-")}
                  style={{
                    paddingTop: index > 0 ? 8 : 0,
                    paddingBottom: 8,
                    borderBottomWidth: 0.5,
                    borderBottomColor: "#D4D4D4",
                    fontFamily: "Poppins",
                    fontSize: 12,
                  }}
                >
                  <Text style={{ fontWeight: "semibold" }}>{label}: </Text>
                  <Text>{value}</Text>
                </Text>
              ))}
            </View>
            <View
              style={{
                flex: 1,
                paddingVertical: 24,
                paddingHorizontal: 36,
                borderRadius: 20,
                backgroundColor: "#f8f9fc",
                textAlign: "center",
              }}
            >
              <Text style={{ fontFamily: "Poppins", fontWeight: "bold", fontSize: 16, color: "#1845a3" }}>
                Blood Pressure Risk
              </Text>
              <Text style={{ fontFamily: "Poppins", fontSize: 10, lineHeight: 1.25, marginTop: 4, marginBottom: 16 }}>
                {bpRiskSelIdx === undefined ? (
                  <Text>Blood pressure is unavailable.</Text>
                ) : (
                  <>
                    <Text>Your Blood Pressure looks to be{"\n"}</Text>
                    <Text style={{ fontWeight: "semibold" }}>{bpRiskSelIdx === 0 ? "within" : "outside"}</Text>
                    <Text> the normal range.</Text>
                  </>
                )}
              </Text>
              <Range ranges={constants["Scores"].ranges} selectedIndex={bpRiskSelIdx} />
              {bpRiskSelIdx !== undefined && <Text style={{ fontFamily: "Poppins", fontSize: 8, marginTop: 12 }}>
                <Text>Detected </Text>
                <Text
                  style={{
                    fontWeight: "semibold",
                    textTransform: "uppercase",
                    color: constants["Scores"].ranges[bpRiskSelIdx].color,
                  }}
                >
                  {constants["Scores"].ranges[bpRiskSelIdx].label} Risk
                </Text>
                <Text> for blood pressure issues.</Text>
              </Text>}
            </View>
          </View>
          <Text style={[styles.title, { marginTop: 24 }]}>Other Health Scores</Text>
          <Score icon={HeartHealth} title="Heart Health" value={result.heart_score} />
          <View style={styles.horizontalLine} />
          <Score icon={MentalWellbeing} title="Mental Wellbeing" value={result.mind_score} />
          <View style={styles.horizontalLine} />
          <Score icon={Physiological} title="Physiological" value={result.physiological_score} />
          <Text style={[styles.title, { marginTop: 24 }]}>Health Parameters</Text>
          <Table
            title="Key Body Vitals"
            data={[
              [
                "Blood Pressure",
                isMetricValueAvailable(result.bp_sys) && isMetricValueAvailable(result.bp_dia)
                  ? `${result.bp_sys}/${result.bp_dia} mmHg`
                  : "Unavailable",
                result.bp_sys !== "--"
                  ? constants["Blood Pressure"].ranges[0].reduce((p, c) => (result.bp_sys > c.point ? c : p), 0)
                  : undefined,
                "90-120/60-80 mmHg",
              ],
              [
                "Respiration Rate",
                displayValue(result.resp_rate, "/min"),
                constants["Respiration Rate"].ranges.reduce((p, c) => (result.resp_rate >= c.point ? c : p)),
                "12-20/min",
              ],
              [
                "Oxygen Saturation",
                displayValue(result.oxy_sat_prcnt, "%"),
                constants["Oxygen Saturation"].ranges.reduce((p, c) => (result.oxy_sat_prcnt >= c.point ? c : p)),
                "95-100%",
              ],
            ]}
          />
          <Disclaimer />
        </View>
        <View style={styles.contentContainer} break>
          <Text style={styles.title}>Health Parameters</Text>
          <Table
            title="Physiological"
            data={[
              [
                "BMI",
                displayValue(result.bmi, " kg/m²"),
                constants["BMI"].ranges.reduce((p, c) => (result.bmi > c.point ? c : p)),
                "18.5-24.9 kg/m²",
              ],
            ]}
          />
          <Table
            containerStyle={{ marginTop: 16 }}
            title="Heart Health"
            data={[
              [
                "Heart Rate",
                displayValue(result.heart_rate, " bpm"),
                constants["Heart Rate"].ranges[result.posture]?.reduce?.((p, c) => (result.heart_rate >= c.point ? c : p)),
                "60-100 bpm",
              ],
              [
                "SDNN",
                displayValue(result.sdnn, " msec", "SDNN"),
                isMetricValueAvailable(result.sdnn) && !isMetricLocked("SDNN", scanDuration)
                  ? constants["SDNN"].ranges.reduce((p, c) => (result.sdnn >= c.point ? c : p))
                  : undefined,
                "60-100 msec",
              ],
              [
                "RMSSD",
                displayValue(result.rmssd, " msec", "RMSSD"),
                isMetricValueAvailable(result.rmssd) && !isMetricLocked("RMSSD", scanDuration)
                  ? constants["RMSSD"].ranges.reduce((p, c) => (result.rmssd >= c.point ? c : p))
                  : undefined,
                "20-89 msec",
              ],
              [
                "PNN50",
                displayValue(result.pNN50_per, "%", "PNN50"),
                isMetricValueAvailable(result.pNN50_per) && !isMetricLocked("PNN50", scanDuration)
                  ? constants["PNN50"].ranges.reduce((p, c) => (result.pNN50_per >= c.point ? c : p))
                  : undefined,
                ">3%",
              ],
              [
                "Cardiac Output",
                displayValue(result.cardiac_out, " L/min"),
                isMetricValueAvailable(result.cardiac_out)
                  ? constants["Cardiac Output"].ranges.reduce((p, c) => (result.cardiac_out >= c.point ? c : p))
                  : undefined,
                "4-8 L/min",
              ],
              [
                "Mean Arterial Pressure",
                displayValue(result.map, " mmHg"),
                isMetricValueAvailable(result.map)
                  ? constants["Mean Arterial Pressure"].ranges.reduce((p, c) => (result.map >= c.point ? c : p))
                  : undefined,
                "70-100 mmHg",
              ],
              [
                "Heart Utilization",
                displayValue(result.heart_utilized, "%"),
                isMetricValueAvailable(result.heart_utilized)
                  ? constants["Heart Utilized"].ranges.reduce((p, c) => (result.heart_utilized >= c.point ? c : p))
                  : undefined,
                <>
                  <Text>{"<50% "}</Text>
                  <Text style={{ fontSize: 8 }}>(At rest)</Text>
                  <Text>{"\n51-85% "}</Text>
                  <Text style={{ fontSize: 8 }}>(Moderate intensity)</Text>
                </>,
              ],
              ["Heart Rate Max", displayValue(result.HRMax, " bpm"), undefined, "Not Applicable"],
              ["Heart Rate Reserve", displayValue(result.HRR, " bpm"), undefined, "Not Applicable"],
              ["Target HR Range", displayValue(result.THRR, " bpm"), undefined, "Not Applicable"],
              [
                "VO2Max",
                displayValue(result.vo2max, " mL/kg/min"),
                undefined,
                <>
                  <Text>{"42.5 ml/kg/min "}</Text>
                  <Text style={{ fontSize: 8 }}>(Male)</Text>
                  <Text>{"\n33 ml/kg/min "}</Text>
                  <Text style={{ fontSize: 8 }}>(Female)</Text>
                </>,
              ],
            ]}
          />
          <Disclaimer />
        </View>
        <View style={styles.contentContainer} break>
          <Text style={styles.title}>Health Risk Description</Text>
          <ParameterInfo
            icon={HeartRate}
            title="Heart Health"
            desc="A measure of the likelihood of developing cardiovascular conditions based on heart rate variability, rhythm abnormalities, and other cardiac parameters."
            ranges={[
              {
                label: "High Risk",
                desc: "Indicates significant irregularities or abnormalities in heart function, requiring immediate medical attention.",
              },
              {
                label: "Moderate Risk",
                desc: "Some irregularities in cardiac health; lifestyle adjustments and periodic monitoring recommended.",
              },
              { label: "Low Risk", desc: "Healthy heart function with minimal or no irregularities observed." },
            ]}
          />
          <ParameterInfo
            icon={BloodPressure}
            title="Blood Pressure"
            desc="A measure of the likelihood of experiencing complications related to hyper-tension or hypo-tension based on systolic and diastolic readings."
            ranges={[
              {
                label: "High Risk",
                desc: "Persistent or severe high/low blood pressure, with potential for serious health issues; medical intervention required.",
              },
              {
                label: "Moderate Risk",
                desc: "Mild irregularities in blood pressure that can be managed with lifestyle changes and monitoring.",
              },
              { label: "Low Risk", desc: "Stable blood pressure within the normal range." },
            ]}
          />
          <ParameterInfo
            icon={StressIndex}
            title="Mental Wellbeing"
            desc="An assessment of stress levels and autonomic nervous system (ANS) balance, reflecting the body's ability to manage stress and maintain mental health stability."
            ranges={[
              {
                label: "High Risk",
                desc: "Chronic stress with significant imbalances in ANS regulation, such as reduced parasympathetic activity or heightened sympathetic dominance; professional intervention strongly recommended.",
              },
              {
                label: "Moderate Risk",
                desc: "Occasional stress or mild ANS imbalances, indicating the need for improved stress management techniques and lifestyle adjustments.",
              },
              {
                label: "Low Risk",
                desc: "Balanced ANS function with effective stress management and resilience, reflecting good mental well-being.",
              },
            ]}
          />
          <ParameterInfo
            icon={VO2MAX}
            title="Physiological"
            desc="A quantitative measure of an individual's overall physiological fitness and well-being. It evaluates key aspects of physical health, including functional capacity, endurance, and overall health status, offering valuable insights into an individual's physical performance and resilience."
            ranges={[
              {
                label: "High Risk",
                desc: "Significant deficiencies in functional capacity, endurance, or overall health status, indicating poor physiological fitness and a heightened risk of health complications; immediate intervention is advised.",
              },
              {
                label: "Moderate Risk",
                desc: "Mild to moderate limitations in physical performance or endurance, suggesting areas for improvement through targeted exercise, nutrition, and lifestyle changes.",
              },
              {
                label: "Low Risk",
                desc: "Optimal physiological fitness with strong functional capacity, endurance, and overall health status, reflecting excellent physical well-being.",
              },
            ]}
          />
        </View>
        <View style={styles.contentContainer} break>
          <Text style={styles.title}>Health Parameters Description</Text>
          <ParameterInfo
            icon={HeartRate}
            title="Heart Rate"
            desc="The heart rate is the number of times heart beats in a minute. A normal resting heart rate for adults ranges from 60 to 100 beats per minute."
          />
          <ParameterInfo
            icon={RespirationRate}
            title="Respiration Rate"
            desc="The respiration rate is the number of breaths taken in a minute. The rate is usually measured when a person is at rest and simply involves counting the number of breaths for one minute by counting how many times the chest rises."
          />
          <ParameterInfo
            icon={OxygenSaturation}
            title="Oxygen Saturation"
            desc="Oxygen saturation (SpO2) is the measurement of how much oxygen the blood is carrying as a percentage of the maximum it could carry."
          />
          <ParameterInfo
            icon={BMI}
            title="BMI"
            desc="BMI is the measure of body fat based on height and weight. The normal range of BMI is 18.5 - 24.9 kg/m²."
          />
          <ParameterInfo
            icon={HRV}
            title="SDNN"
            desc="The standard deviation of all of the NN intervals (the time intervals between each heartbeat). Higher numbers usually indicate that your body is coping better with stress."
          />
          <ParameterInfo
            icon={HRV}
            title="RMSSD"
            desc="The root mean square of successive intervals between normal heartbeats (RMSSD). RMSSD is obtained by first calculating each successive time intervals between heartbeats in msec."
          />
          <ParameterInfo
            icon={HRV}
            title="PNN50"
            desc="PNN50 is the proportion of adjacent N-N intervals differing by more than 50 msec. pNN50 lower than 3% is considered indicative of high risk."
          />
        </View>
        <View style={styles.contentContainer} break>
          <Text style={styles.title}>Health Parameters Description</Text>
          <ParameterInfo
            icon={CardiacOutput}
            title="Cardiac Output"
            desc="The amount of blood heart pumps through the circulatory system in a minute. Cardiac output in humans is generally 4-8 L/min in an at-rest."
          />
          <ParameterInfo
            icon={MAP}
            title="Mean Arterial Pressure"
            desc="MAP is the average arterial pressure throughout one heart cycle. MAP anything between 70 and 100 mmHg is considered to be normal."
          />
          <ParameterInfo
            icon={HeartUtilization}
            title="Heart Utilization"
            desc="Heart utilization is the percentage of overall heart usage to its maximum peak heart rate. Generally < 50% utilization at rest and 51-85% utilization at moderate intensity is considered as normal."
          />
          <ParameterInfo
            icon={HRRelated}
            title="Heart Rate Max"
            desc="The highest number of beats the heart can pump per minute is the maximum heart rate."
          />
          <ParameterInfo
            icon={HRRelated}
            title="Heart Rate Reserve"
            desc="Heart rate reserve is the difference between your maximum (peak) heart rate and your resting heart rate."
          />
          <ParameterInfo
            icon={HRRelated}
            title="Target Heart Rate"
            desc="To achieve a certain level of exertion, there is a minimum number of heartbeats required within a specific timeframe."
          />
          <ParameterInfo
            icon={VO2MAX}
            title="VO2Max"
            desc="VO2Max is the amount of oxygen the body uses during exercise. It is a measure-ment to understand Physical fitness."
          />
        </View>
        <View fixed style={styles.footer}>
          <Text style={styles.footerText}>
            The Report generated by CarePlix on behalf of Indusind General Insurance.
          </Text>
        </View>
      </Page>
      <Page wrap={false} style={{ backgroundColor: "#f8faff" }}>
        <View style={{ height: "100%", width: "100%", flexDirection: "column", alignItems: "center", justifyContent: "space-between", paddingVertical: 48, paddingHorizontal: 40 }}>
          <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 16, width: "100%" }}>
            <Image source={CompanyLogo} style={{ height: 48 }} />
            <View style={{ width: 1, height: 36, backgroundColor: "#d1d5db" }} />
            <View>
              <Text style={{ fontFamily: "Poppins", fontSize: 8, color: "#6b7280" }}>Powered by</Text>
              <Image source={CareplixLogo} style={{ height: 20 }} />
            </View>
          </View>
          <View style={{ alignItems: "center", paddingHorizontal: 24 }}>
            <Text style={{ fontFamily: "Poppins", fontWeight: "bold", fontSize: 28, color: "#1845a3", textAlign: "center", marginBottom: 24 }}>
              Thank You!
            </Text>
            <View style={{ width: 60, height: 3, borderRadius: 2, backgroundColor: "#ED1C24", marginBottom: 24 }} />
            <Text style={{ fontFamily: "Poppins", fontWeight: "medium", fontSize: 14, color: "#374151", textAlign: "center", lineHeight: 1.6 }}>
              Thanks for doing your Digital Health Assessment!
            </Text>
            <Text style={{ fontFamily: "Poppins", fontSize: 12, color: "#6b7280", textAlign: "center", lineHeight: 1.6, marginTop: 16 }}>
              Kindly connect with the advisor/partner to protect your health and other services offered by IGICL.
            </Text>
            {agentName ? (
              <View style={{ marginTop: 32, paddingVertical: 16, paddingHorizontal: 32, backgroundColor: "#ffffff", borderRadius: 12, borderWidth: 1, borderColor: "#e5e7eb", alignItems: "center" }}>
                <Text style={{ fontFamily: "Poppins", fontSize: 9, color: "#9ca3af", marginBottom: 4 }}>Your Advisor</Text>
                <Text style={{ fontFamily: "Poppins", fontWeight: "bold", fontSize: 18, color: "#1845a3" }}>{agentName}</Text>
              </View>
            ) : null}
          </View>
          <View style={{ alignItems: "center" }}>
            <Text style={{ fontFamily: "Poppins", fontSize: 7, color: "#9ca3af", textAlign: "center" }}>
              IndusInd General Insurance Company Limited | Powered by CarePlix
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

const Range = ({
  ranges = [
    { point: 0, label: "Start", color: "#ccc" },
    { point: 100, label: "End", color: "#000" },
  ],
  selectedIndex,
  className = "",
}) => {
  selectedIndex -= ranges.length - 1 === selectedIndex;
  return (
    <div className={`relative grid grid-flow-col auto-cols-fr ${className}`}>
      {ranges.slice(0, -1).map((rangeItem, index) => (
        <div key={`range-${index}`} className="flex flex-col justify-end">
          {index === selectedIndex && (
            <Caret className="self-center flex-shrink-0 w-2 h-1" style={{ color: ranges[selectedIndex].color }} />
          )}
          <h6
            className="text-center text-xxs font-medium uppercase truncate"
            style={{ color: index === selectedIndex ? ranges[selectedIndex].color : "#9ca3af" }}
          >
            {rangeItem.label}
          </h6>
          <div
            className="my-1 h-1.5 rounded-full"
            style={{ backgroundColor: index === selectedIndex ? ranges[selectedIndex].color : "#e5e7eb" }}
          />
          <p className="text-gray-400 text-xxs">{rangeItem.point}</p>
        </div>
      ))}
      <p className="absolute bottom-0 right-0 text-gray-400 text-xxs">{ranges[ranges.length - 1].point}</p>
    </div>
  );
};

const ListItemContainer = ({
  disabled = false,
  label = "",
  defaultExpanded = false,
  title,
  desc,
  className = "",
  ...props
}) => {
  return (
    <Disclosure
      defaultOpen={defaultExpanded}
      as="div"
      className="px-5 py-4 rounded-2xl bg-white border border-gray-100 shadow-sm"
    >
      <Disclosure.Button as="div" disabled={disabled}>
        {({ open }) => [
          <div key="list-item-container-head" className="flex items-center justify-between space-x-2">
            <div className="flex items-center space-x-2.5">
              <div className="w-1 h-5 rounded-full bg-gradient-to-b from-[#1845a3] to-[#ED1C24]" />
              <h2 className="text-sm font-semibold text-gray-800">{label}</h2>
            </div>
            <Chevron
              className={`h-3.5 w-3.5 text-gray-400 transition-transform duration-300 ${open ? "rotate-180" : "rotate-0"}`}
            />
          </div>,
          <Fragment key="list-item-container-body">{props.children}</Fragment>,
        ]}
      </Disclosure.Button>
      <Disclosure.Panel>
        {typeof title === "string" && (
          <h5 className="mt-3 text-xs font-semibold text-[#1845a3]">{title}</h5>
        )}
        {typeof desc === "string" && (
          <p className="mt-1.5 text-xxs text-gray-500 leading-relaxed whitespace-pre-line">{desc}</p>
        )}
      </Disclosure.Panel>
    </Disclosure>
  );
};

const ScoreRagBar = ({ riskIndex }) => {
  const ranges = constants["Scores"].ranges;
  return (
    <div className="flex">
      {ranges.map((r, i) => (
        <div key={i} className="flex flex-col items-center" style={{ flex: 1 }}>
          <span
            className="text-center leading-none mb-1 font-medium uppercase"
            style={{
              color: r.color,
              fontSize: "7px",
            }}
          >
            {r.label}
          </span>
          <div
            className="self-stretch h-1.5"
            style={{
              backgroundColor: r.color,
              borderRadius: i === 0 ? "999px 0 0 999px" : i === ranges.length - 1 ? "0 999px 999px 0" : "0",
            }}
          />
          {i === riskIndex && (
            <svg className="flex-shrink-0 w-2.5 h-1.5 mt-0.5" viewBox="0 0 10 5" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 5H10L4.92754 0L0 5Z" fill="currentColor" style={{ color: r.color }} />
            </svg>
          )}
        </div>
      ))}
    </div>
  );
};

const ScoreItem = ({ score, result }) => {
  const [showInfo, setShowInfo] = useState(false);
  const available = isMetricValueAvailable(score.value) && !score.locked;

  const getRiskIndex = () => {
    if (score.title === "Blood Pressure") {
      const sys = score.value;
      return sys !== "--"
        ? sys < 108 || sys > 135 ? 2 : sys < 115 || sys > 125 ? 1 : 0
        : 0;
    }
    return score.value >= 75 ? 0 : score.value >= 50 ? 1 : 2;
  };

  const riskIndex = getRiskIndex();
  const selectedScoreRange = constants["Scores"].ranges[riskIndex];

  const isBP = score.title === "Blood Pressure";
  const sys = isBP ? score.value : null;
  const dia = isBP ? score.dia : null;
  const selectedSYSRangeIndex = isBP && sys !== "--"
    ? constants["Blood Pressure"].ranges[0].reduce((p, c, ci) => (sys > c.point ? ci : p), 0)
    : undefined;
  const selectedDIARangeIndex = isBP && dia !== "--"
    ? constants["Blood Pressure"].ranges[1].reduce((p, c, ci) => (dia > c.point ? ci : p), 0)
    : undefined;

  return (
    <div className="px-5 py-4 border-b border-gray-100 last:border-b-0">
      <div className="flex items-center">
        <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-[#1845a3] to-[#ED1C24] flex items-center justify-center shadow-sm">
          <img className="h-5 w-5 brightness-0 invert" src={score.logo} alt={score.title} />
        </div>
        <h2 className="grow mx-3 text-sm font-semibold text-gray-800 leading-tight">{score.title}</h2>
        {!available ? (
          <span className="max-w-[145px] text-right text-xxs font-medium text-gray-400">
            {score.locked ? SHORT_SCAN_LOCK_MESSAGE : "Unavailable"}
          </span>
        ) : <div className="shrink-0 flex items-center space-x-2">
          <div className="flex items-center space-x-1.5">
            <div className="relative h-3 w-3 rounded-full" style={{ backgroundColor: selectedScoreRange.color }}>
              <div
                className="absolute inset-0 rounded-full opacity-40"
                style={{ backgroundColor: selectedScoreRange.color, animation: "pulsate 1.5s infinite ease-out" }}
              />
            </div>
            <span className="text-xs font-semibold" style={{ color: selectedScoreRange.color }}>
              {selectedScoreRange.label} Risk
            </span>
          </div>
          <button
            type="button"
            onClick={() => setShowInfo((p) => !p)}
            className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${showInfo ? "bg-gradient-to-r from-[#1845a3] to-[#ED1C24]" : "bg-gray-100"}`}
          >
            <Info className={`h-3 w-3 ${showInfo ? "text-white" : "text-gray-400"}`} />
          </button>
        </div>}
      </div>
      {available && isBP && (
        <div className="mt-3 ml-[52px]">
          <ScoreRagBar riskIndex={riskIndex} />
        </div>
      )}
      {available && showInfo && (
        <div className="mt-3 p-3 rounded-xl bg-gradient-to-br from-[#1845a3]/5 to-[#ED1C24]/5 border border-[#1845a3]/10">
          {isBP && sys !== "--" && selectedSYSRangeIndex !== undefined && selectedDIARangeIndex !== undefined ? (
            <>
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <h4 className="text-lg font-bold text-gray-800">{sys}</h4>
                    <h5 className="text-xxs text-gray-400">{constants["Blood Pressure"].unit}</h5>
                    <p className="text-xxs text-[#1845a3] font-medium mt-0.5">Systolic</p>
                  </div>
                  <div className="flex-grow ml-3">
                    <Range ranges={constants["Blood Pressure"].ranges[0]} selectedIndex={selectedSYSRangeIndex} />
                  </div>
                </div>
              </div>
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <h4 className="text-lg font-bold text-gray-800">{dia}</h4>
                    <h5 className="text-xxs text-gray-400">{constants["Blood Pressure"].unit}</h5>
                    <p className="text-xxs text-[#1845a3] font-medium mt-0.5">Diastolic</p>
                  </div>
                  <div className="flex-grow ml-3">
                    <Range ranges={constants["Blood Pressure"].ranges[1]} selectedIndex={selectedDIARangeIndex} />
                  </div>
                </div>
              </div>
            </>
          ) : null}
          <p className="text-gray-500 text-justify text-xxs leading-relaxed whitespace-pre-line">{score.desc}</p>
        </div>
      )}
    </div>
  );
};

const Scores = ({ list = [], result }) => {
  return (
    <div className="rounded-2xl overflow-hidden shadow-lg">
      <div className="bg-gradient-to-br from-[#1845a3] via-[#3545B9] to-[#ED1C24] px-5 py-4">
        <h3 className="text-white text-sm font-semibold tracking-wide">Health Risk Overview</h3>
        <p className="text-white/60 text-xxs mt-0.5">Tap Info to learn more about each metric</p>
      </div>
      <div className="bg-white">
        <div>
          {list.map((score, index) => (
            <ScoreItem key={`score-${index}`} score={score} result={result} />
          ))}
        </div>
      </div>
    </div>
  );
};

const ScanResult = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { agentInfo } = useAuth();
  const result = state?.result;
  const scanDuration = getScanDuration(result, state?.scanDuration);
  const reportPDF = useMemo(() => {
    const ageDifMs = Date.now() - new Date(result?.metadata?.physiological_scores?.dob).getTime();
    const ageDate = new Date(ageDifMs);
    const age = Math.abs(ageDate.getUTCFullYear() - 1970);
    const gender = result?.metadata?.physiological_scores?.gender ?? "";
    const gen = `${gender.slice(0, 1).toUpperCase()}${gender.slice(1)}`;
    const vitalsData = {
      timestamp: new Date(result?.entry_time?.replace?.(" ", "T") ?? 0),
      posture: result?.posture?.toLowerCase?.(),
      mind_score: result?.metadata.mental_wellbeing_score,
      heart_score: result?.metadata.overall_heart_score,
      physiological_score: result?.metadata.physiological_score,
      bp_sys: result?.vitals.bp_sys,
      bp_dia: result?.vitals.bp_dia,
      resp_rate: result?.vitals.resp_rate,
      oxy_sat_prcnt: result?.vitals.oxy_sat_prcnt,
      bmi: result?.metadata.physiological_scores.bmi,
      heart_rate: result?.vitals.heart_rate,
      sdnn: result?.metadata.heart_scores.sdnn !== "--" ? Math.round(result?.metadata.heart_scores.sdnn) : "--",
      rmssd: result?.metadata.heart_scores.rmssd !== "--" ? Math.round(result?.metadata.heart_scores.rmssd) : "--",
      pNN50_per: result?.metadata.heart_scores.pNN50_per,
      cardiac_out: result?.metadata.cardiovascular.cardiac_out,
      map: result?.metadata.cardiovascular.map,
      heart_utilized: result?.metadata.heart_scores.heart_utilized,
      HRMax: result?.metadata.heart_scores.HRMax,
      HRR: result?.metadata.heart_scores.HRR,
      THRR: result?.metadata.heart_scores.THRR,
      vo2max: result?.metadata.physiological_scores.vo2max,
    };
    return (
      <PDFReport
        referralCode={result?.notes?.referral_code ?? ""}
        personName={result?.notes?.person_name ?? ""}
        agentName={agentInfo?.agent_name ?? ""}
        relation={result?.notes?.relation ?? ""}
        age={age}
        gender={gen}
        height={result?.metadata?.physiological_scores?.height}
        weight={result?.metadata?.physiological_scores?.weight}
        result={vitalsData}
        scanDuration={scanDuration}
      />
    );
  }, [result, agentInfo, scanDuration]);

  const [modalState, setModalState] = useState({ visible: false, title: "", desc: "" });
  const closeModal = () => setModalState({ visible: false, title: "", desc: "" });

  const [emailAddress, setEmailAddress] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const [showBackConfirm, setShowBackConfirm] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [insuranceForm, setInsuranceForm] = useState({
    hasHealthInsurance: null,
    hasMotorInsurance: null,
    motorType: "",
    hasPaCiCover: null,
    hasHouseInsurance: null,
  });
  const [insuranceSubmitting, setInsuranceSubmitting] = useState(false);
  const [insuranceSubmitted, setInsuranceSubmitted] = useState(false);
  const [pdfSentOrDownloaded, setPdfSentOrDownloaded] = useState(false);

  useEffect(() => {
    const pushState = () => window.history.pushState({ preventBack: true }, "", window.location.href);
    const handlePopState = () => {
      pushState();
      setShowBackConfirm(true);
    };
    pushState();
    pushState();
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const handleSendEmail = useCallback(async () => {
    const trimEmail = emailAddress.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimEmail)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setEmailError("");
    setEmailSending(true);
    try {
      const pdfBlob = await pdf(reportPDF).toBlob();
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onloadend = () => resolve(reader.result.split(",")[1]);
        reader.readAsDataURL(pdfBlob);
      });
      const fileName = `CarePlix-ScanResult-Report-${new Date(result?.entry_time?.replace?.(" ", "T") ?? 0).toISOString()}.pdf`;
      const resp = await fetch("/api/send-report-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: trimEmail,
          pdfBase64: base64,
          fileName,
          personName: result?.notes?.person_name || "",
        }),
      });
      const data = await resp.json();
      if (data.success) {
        setEmailSent(true);
        setPdfSentOrDownloaded(true);
      } else {
        setEmailError(data.message || "Failed to send email.");
      }
    } catch (err) {
      setEmailError("Failed to send email. Please try again.");
    } finally {
      setEmailSending(false);
    }
  }, [emailAddress, reportPDF, result]);

  const handleDownloadPDF = useCallback(async () => {
    setDownloading(true);
    try {
      const pdfBlob = await pdf(reportPDF).toBlob();
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `CarePlix-ScanResult-Report-${new Date(result?.entry_time?.replace?.(" ", "T") ?? 0).toISOString()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setPdfSentOrDownloaded(true);
    } catch (err) {
      console.error("PDF download error:", err);
    } finally {
      setDownloading(false);
    }
  }, [reportPDF, result]);

  const handleInsuranceSubmit = useCallback(async () => {
    setInsuranceSubmitting(true);
    try {
      await fetch("/api/insurance-questionnaire", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone_number: result?.notes?.phone_number || "",
          referral_code: result?.notes?.referral_code || "",
          scan_id: result?.scan_id || "",
          has_health_insurance: insuranceForm.hasHealthInsurance,
          has_motor_insurance: insuranceForm.hasMotorInsurance,
          motor_insurance_type: insuranceForm.motorType,
          has_pa_ci_cover: insuranceForm.hasPaCiCover,
          has_house_insurance: insuranceForm.hasHouseInsurance,
        }),
      });
      setInsuranceSubmitted(true);
    } catch (err) {
      console.error("Insurance questionnaire error:", err);
    } finally {
      setInsuranceSubmitting(false);
    }
  }, [result, insuranceForm]);

  return result?.scan_id?.length > 0 ? (
    <section className="pb-8">
      <div className="mb-5 px-5 py-4 bg-gradient-to-r from-[#1845a3] to-[#ED1C24] flex items-center space-x-3">
        <button className="flex-shrink-0 p-1.5 rounded-lg bg-white/15" type="button" onClick={() => setShowBackConfirm(true)}>
          <BackArrow className="h-3 w-3 text-white" />
        </button>
        <div>
          <h3 className="text-white text-sm font-semibold">Scan Result</h3>
          <p className="text-white/60 text-xxs">Your health assessment report</p>
        </div>
      </div>

      <div className="px-5 space-y-5">
      <Scores
        result={result}
        list={[
          {
            title: "Blood Pressure",
            desc: "Blood pressure risk refers to the potential health problems caused by abnormal blood pressure levels. High blood pressure (hypertension) increases the risk of heart disease, stroke, and kidney damage, while low blood pressure (hypotension) can cause dizziness and fainting. Risk factors include age, obesity, and poor lifestyle choices. Regular monitoring and healthy habits help manage these risks.\n\nPlease correlate clinically.",
            value: result?.vitals.bp_sys,
            dia: result?.vitals.bp_dia,
            logo: BloodPressureScore,
          },
          {
            title: "Heart Health",
            desc: "The Heart Health provides a comprehensive assessment of an individual's cardiac health status, helps to monitor cardiac function, track progress over time, and tailor interventions or treatment plans accordingly. A low risk heart health typically indicates better cardiac health and function, while a high risk may suggest the need for further evaluation or intervention to improve cardiovascular well-being.",
            value: result?.metadata.overall_heart_score,
            logo: HeartHealthScore,
          },
          {
            title: "Mental Wellbeing",
            desc: "The Mental Wellbeing Score, derived from an individual's stress level, mood insights, sleep quality provides a quantitative measure of an individual's overall mental health. It encompasses various dimensions of mental wellness, including emotional resilience, coping mechanisms, and overall psychological functioning.",
            value: result?.metadata.mental_wellbeing_score,
            locked: isMetricLocked("Mental Wellbeing", scanDuration),
            logo: MentalWellbeingScore,
          },
          {
            title: "Physiological",
            desc: "The Physiological Score, also known as the Physio Fitness Score, is a quantitative measure that evaluates an individual's overall physiological fitness and well-being. It assesses various aspects of physical health and performance, providing insights into an individual's functional capacity, endurance, and overall health status.",
            value: result?.metadata.physiological_score,
            logo: PhysiologicalScore,
          },
        ]}
      />
      <div className="flex items-center space-x-2.5">
        <div className="w-1.5 h-6 rounded-full bg-gradient-to-b from-[#1845a3] to-[#ED1C24]" />
        <h2 className="text-gray-800 text-base font-bold">Detailed Report</h2>
      </div>
      <Tab.Group>
        <Tab.List className="flex flex-wrap gap-2">
          {[
            "Key Body Vitals",
            "Heart Health",
            "Physiological",
            ...(isMetricLocked("Stress Level", scanDuration) ||
            isMetricValueAvailable(result?.metadata?.heart_scores?.stress_index)
              ? ["Stress & ANS"]
              : []),
          ].map((label, index) => (
            <Tab
              key={`tab-header-${index}`}
              className={({ selected }) =>
                `px-4 py-2 rounded-full text-xxs font-semibold whitespace-nowrap outline-none transition-all duration-200 ${
                  selected
                    ? "bg-gradient-to-r from-[#1845a3] to-[#ED1C24] text-white shadow-md"
                    : "bg-white text-gray-500 border border-gray-200 hover:border-[#1845a3]/30"
                }`
              }
            >
              {label}
            </Tab>
          ))}
        </Tab.List>
        <Tab.Panels className="mt-4 space-y-4">
          <Tab.Panel className="space-y-4">
            {[
              { label: "Heart Rate", value: result?.vitals.heart_rate },
              { label: "Respiration Rate", value: result?.vitals.resp_rate },
              { label: "Oxygen Saturation", value: result?.vitals.oxy_sat_prcnt },
            ].map((item, index) => {
              const available = isMetricValueAvailable(item.value);
              const ranges =
                item.label === "Heart Rate"
                  ? constants[item.label].ranges[result?.posture ?? "resting"]
                  : constants[item.label].ranges;
              const selectedRangeIndex = available
                ? ranges.reduce((p, c, ci) => (item.value >= c.point ? ci : p), 0)
                : undefined;
              return (
                <ListItemContainer
                  key={`section-item-${index}`}
                  label={item.label}
                  title={available ? `Your ${item.label} is ${ranges[selectedRangeIndex].label}` : "Unavailable"}
                  desc={available ? constants[item.label].info + "\n\n" + ranges[selectedRangeIndex].desc : ""}
                  disabled={!available}
                >
                  <div className="py-3 flex items-center justify-between space-x-2">
                    <div className="flex-shrink-0 basis-1/4 text-center">
                      <h4 className="text-2xl font-bold text-gray-800">{available ? item.value : "Unavailable"}</h4>
                      <h5 className="text-xs text-gray-400 mt-0.5">{constants[item.label].unit}</h5>
                    </div>
                    <Range className="flex-grow" ranges={ranges} selectedIndex={selectedRangeIndex} />
                  </div>
                </ListItemContainer>
              );
            })}
            {(() => {
              const sys = result?.vitals.bp_sys;
              const dia = result?.vitals.bp_dia;
              const selectedSYSRangeIndex =
                isMetricValueAvailable(sys)
                  ? constants["Blood Pressure"].ranges[0].reduce((p, c, ci) => (sys > c.point ? ci : p), 0)
                  : undefined;
              const selectedDIARangeIndex =
                isMetricValueAvailable(dia)
                  ? constants["Blood Pressure"].ranges[1].reduce((p, c, ci) => (dia > c.point ? ci : p), 0)
                  : undefined;
              return (
                <ListItemContainer
                  key="section-item-3"
                  label="Blood Pressure"
                  title={`Your Blood Pressure is ${constants["Blood Pressure"].ranges[0][selectedSYSRangeIndex]?.label}`}
                  desc={
                    constants["Blood Pressure"].info +
                    "\n\n" +
                    constants["Blood Pressure"].ranges[0][selectedSYSRangeIndex]?.desc
                  }
                  disabled={selectedSYSRangeIndex === undefined || selectedDIARangeIndex === undefined}
                >
                  <div className="py-3 flex items-center justify-between space-x-2">
                    <div className="flex-shrink-0 basis-1/4 text-center">
                      <h4 className="text-2xl font-bold text-gray-800">{isMetricValueAvailable(sys) ? sys : "Unavailable"}</h4>
                      <h5 className="text-xxs text-gray-400">{constants["Blood Pressure"].unit}</h5>
                      <p className="text-xxs text-[#1845a3] font-medium mt-0.5">Systolic</p>
                    </div>
                    <Range
                      className="flex-grow"
                      ranges={constants["Blood Pressure"].ranges[0]}
                      selectedIndex={selectedSYSRangeIndex}
                    />
                  </div>
                  <div className="py-3 flex items-center justify-between space-x-2">
                    <div className="flex-shrink-0 basis-1/4 text-center">
                      <h4 className="text-2xl font-bold text-gray-800">{isMetricValueAvailable(dia) ? dia : "Unavailable"}</h4>
                      <h5 className="text-xxs text-gray-400">{constants["Blood Pressure"].unit}</h5>
                      <p className="text-xxs text-[#1845a3] font-medium mt-0.5">Diastolic</p>
                    </div>
                    <Range
                      className="flex-grow"
                      ranges={constants["Blood Pressure"].ranges[1]}
                      selectedIndex={selectedDIARangeIndex}
                    />
                  </div>
                </ListItemContainer>
              );
            })()}
          </Tab.Panel>
          <Tab.Panel className="space-y-4">
            {[
              {
                label: "SDNN",
                value: result?.metadata.heart_scores.sdnn !== "--" ? Math.round(result?.metadata.heart_scores.sdnn) : "--",
              },
              {
                label: "RMSSD",
                value: result?.metadata.heart_scores.rmssd !== "--" ? Math.round(result?.metadata.heart_scores.rmssd) : "--",
              },
              { label: "PNN50", value: result?.metadata.heart_scores.pNN50_per },
              { label: "Cardiac Output", value: result?.metadata.cardiovascular.cardiac_out },
              { label: "Mean Arterial Pressure", value: result?.metadata.cardiovascular.map },
              { label: "Heart Utilized", value: result?.metadata.heart_scores.heart_utilized },
            ].map((item, index) => {
              const locked = isMetricLocked(item.label, scanDuration);
              const available = isMetricValueAvailable(item.value) && !locked;
              const selectedRangeIndex =
                available
                  ? constants[item.label].ranges.reduce((p, c, ci) => (item.value >= c.point ? ci : p), 0)
                  : undefined;
              return (
                <ListItemContainer
                  key={`section-item-${index}`}
                  label={item.label}
                   title={available ? `Your ${item.label} is ${constants[item.label].ranges[selectedRangeIndex]?.label}` : ""}
                   desc={available ? constants[item.label].info + "\n\n" + constants[item.label].ranges[selectedRangeIndex]?.desc : ""}
                   disabled={!available}
                >
                  <div className="py-3 flex items-center justify-between space-x-2">
                    <div className="flex-shrink-0 basis-1/4 text-center">
                       <h4 className={`font-bold ${available ? "text-2xl text-gray-800" : "text-xs text-gray-400"}`}>
                         {locked ? SHORT_SCAN_LOCK_MESSAGE : available ? item.value : "Unavailable"}
                       </h4>
                      <h5 className="text-xs text-gray-400 mt-0.5">{constants[item.label].unit}</h5>
                    </div>
                    <Range className="flex-grow" ranges={constants[item.label].ranges} selectedIndex={selectedRangeIndex} />
                  </div>
                </ListItemContainer>
              );
            })}
            <div className="grid grid-cols-2 gap-3">
              {[
                {
                  label: "Heart Rate Max",
                  value: result?.metadata.heart_scores.HRMax,
                  icon: <svg className="w-4 h-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>,
                },
                {
                  label: "Heart Rate Reserve",
                  value: result?.metadata.heart_scores.HRR,
                  icon: <svg className="w-4 h-4 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>,
                },
                {
                  label: "Target HR Range",
                  value: result?.metadata.heart_scores.THRR,
                  icon: <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
                },
                {
                  label: "VO₂ Max",
                  value: result?.metadata.physiological_scores.vo2max,
                  icon: <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
                },
              ].map((item, index) => (
                <button
                  type="button"
                  key={`section-smallitem-${index}`}
                  className="px-4 py-4 rounded-2xl bg-white border border-gray-100 shadow-sm flex flex-col items-stretch justify-between text-left transition-shadow hover:shadow-md"
                  onClick={() =>
                    setModalState({
                      visible: true,
                      title: item.label,
                      desc: constants[item.label].description,
                    })
                  }
                >
                  <div className="flex items-center justify-between">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#1845a3]/10 to-[#ED1C24]/10 flex items-center justify-center">
                      {item.icon}
                    </div>
                    <Info className="h-3.5 w-3.5 text-gray-300" />
                  </div>
                  <h2 className="mt-3 text-xxs font-medium text-gray-500">{item.label}</h2>
                  <div className="mt-1 break-all">
                    <h4 className="inline text-xl font-bold text-gray-800">
                      {isMetricValueAvailable(item.value) ? item.value : "Unavailable"}
                    </h4>
                    <span className="ml-1 text-xxs text-gray-400">{constants[item.label].unit}</span>
                  </div>
                </button>
              ))}
            </div>
          </Tab.Panel>
          <Tab.Panel className="space-y-4">
            {(() => {
              const bmi = result?.metadata.physiological_scores.bmi;
              const bmiAvailable = isMetricValueAvailable(bmi);
              const selectedBMIRangeIndex = bmiAvailable
                ? constants.BMI.ranges.reduce((p, c, ci) => (bmi > c.point ? ci : p), 0)
                : undefined;
              return (
                <ListItemContainer
                  label="BMI"
                  title={bmiAvailable ? `Your BMI is ${constants.BMI.ranges[selectedBMIRangeIndex].label}` : ""}
                  desc={bmiAvailable ? constants.BMI.info + "\n\n" + constants.BMI.ranges[selectedBMIRangeIndex].desc : ""}
                  disabled={!bmiAvailable}
                >
                  <div className="py-3 flex items-center justify-between space-x-2">
                    <div className="flex-shrink-0 basis-1/4 text-center">
                      <h4 className="text-2xl font-bold text-gray-800">{bmiAvailable ? bmi : "Unavailable"}</h4>
                      <h5 className="text-xs text-gray-400 mt-0.5">{constants.BMI.unit}</h5>
                    </div>
                    <Range className="flex-grow" ranges={constants.BMI.ranges} selectedIndex={selectedBMIRangeIndex} />
                  </div>
                </ListItemContainer>
              );
            })()}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                {[
                  {
                    label: "Blood Volume",
                    value: result?.metadata.physiological_scores.bloodvolume,
                    icon: <svg className="w-4 h-4 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" /></svg>,
                  },
                  {
                    label: "Total Body Water",
                    value: result?.metadata.physiological_scores.tbw,
                    icon: <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707" /></svg>,
                  },
                  ...(result?.metadata.physiological_scores.cal_carb !== "--" &&
                  result?.metadata.physiological_scores.cal_fat !== "--"
                    ? [
                        {
                          label: "Calorie from Carb",
                          value:
                            !(result?.metadata.physiological_scores.cal_carb >= 0) ||
                            !(result?.metadata.physiological_scores.cal_carb <= 100)
                              ? "--"
                              : result.metadata.physiological_scores.cal_carb,
                          icon: <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
                        },
                        {
                          label: "Calorie from Fat",
                          value:
                            !(result?.metadata.physiological_scores.cal_fat >= 0) ||
                            !(result?.metadata.physiological_scores.cal_fat <= 100)
                              ? "--"
                              : result.metadata.physiological_scores.cal_fat,
                          icon: <svg className="w-4 h-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" /></svg>,
                        },
                      ]
                    : []),
                ].map((item, index) => (
                  <button
                    type="button"
                    key={`section-smallitem-${index}`}
                    className="px-4 py-4 rounded-2xl bg-white border border-gray-100 shadow-sm flex flex-col items-stretch justify-between text-left transition-shadow hover:shadow-md"
                    onClick={() => setModalState({ visible: true, title: item.label, desc: constants[item.label].description })}
                  >
                    <div className="flex items-center justify-between">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#1845a3]/10 to-[#ED1C24]/10 flex items-center justify-center">
                        {item.icon}
                      </div>
                      <Info className="h-3.5 w-3.5 text-gray-300" />
                    </div>
                    <h2 className="mt-3 text-xxs font-medium text-gray-500">{item.label}</h2>
                    <div className="mt-1 break-all">
                      <h4 className="inline text-xl font-bold text-gray-800">
                        {isMetricValueAvailable(item.value) ? item.value : "Unavailable"}
                      </h4>
                      <span className="ml-1 text-xxs text-gray-400">{constants[item.label].unit}</span>
                    </div>
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "Body Water %", value: result?.metadata.physiological_scores.tbwp },
                  { label: "Total Body Fat", value: result?.metadata.physiological_scores.bodyfat },
                  { label: "Body Weight", value: result?.metadata.physiological_scores.weight },
                ].map((item, index) => (
                  <button
                    type="button"
                    key={`section-smallitem-${index}`}
                    className="px-3 py-4 rounded-2xl bg-white border border-gray-100 shadow-sm flex flex-col items-stretch justify-between text-left"
                    onClick={() => setModalState({ visible: true, title: item.label, desc: constants[item.label].description })}
                  >
                    <div className="flex items-start justify-between space-x-0.5">
                      <h2 className="text-xxs font-medium text-gray-500 leading-tight">{item.label}</h2>
                      <Info className="flex-shrink-0 h-3 w-3 text-gray-300" />
                    </div>
                    <div className="mt-2.5 break-all">
                      <h4 className="inline text-lg font-bold text-gray-800">
                        {isMetricValueAvailable(item.value) ? item.value : "Unavailable"}
                      </h4>
                      <span className="ml-0.5 text-xxs text-gray-400">{constants[item.label].unit}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </Tab.Panel>
          {(isMetricLocked("Stress Level", scanDuration) ||
            isMetricValueAvailable(result?.metadata?.heart_scores?.stress_index)) && (
            <Tab.Panel className="space-y-4">
              {isMetricLocked("Stress Level", scanDuration) ? (
                <div className="p-6 rounded-2xl bg-white border border-gray-100 shadow-sm text-center">
                  <svg className="mx-auto h-7 w-7 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2h-1V7a5 5 0 00-10 0v4H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                  </svg>
                  <p className="mt-3 text-xs font-semibold text-gray-600">{SHORT_SCAN_LOCK_MESSAGE}</p>
                  <p className="mt-1 text-xxs text-gray-400">HRV, stress and ANS insights require the complete scan.</p>
                </div>
              ) : (() => {
                const stressLevel = result?.metadata.heart_scores.stress_index;
                const selectedRangeIndex = constants["Stress Level"].ranges.reduce(
                  (p, c, ci) => (stressLevel > c.point ? ci : p),
                  0
                );
                const selectedRange = constants["Stress Level"].ranges[selectedRangeIndex];
                let detailTitle = "";
                let detailDescription = "";
                if ((result?.metadata?.heart_scores?.rmssd ?? "--") !== "--") {
                  if (result.metadata.heart_scores.rmssd < 65) {
                    if (result.vitals.heart_rate > 100 || result.vitals.bp_sys > 130 || result.vitals.bp_dia > 90) {
                      detailTitle = "More Sympathetic Interactions";
                      detailDescription =
                        "Although HRV manifests as a function of your heart rate, it actually originates from your nervous system. Your autonomic nervous system, which controls the involuntary aspects of your physiology, has two branches, parasympathetic (deactivating) and sympathetic (activating).\n\nThe sympathetic nervous system (often called “fight or flight”) reflects responses to things like stress and exercise, and increases your heart rate and blood pressure.";
                    } else {
                      detailTitle = "More Parasympathetic Interactions";
                      detailDescription =
                        "Although HRV manifests as a function of your heart rate, it actually originates from your nervous system. Your autonomic nervous system, which controls the involuntary aspects of your physiology, has two branches, parasympathetic (deactivating) and sympathetic (activating)\n\nThe parasympathetic nervous system (often referred to as “rest and digest”) handles inputs from internal organs, like digestion or your fingernails and hair growing.";
                    }
                  } else {
                    detailTitle = "Balanced ANS Interactions";
                    detailDescription =
                      "When you have high heart rate variability, it means that your body is responsive to both sets of inputs (parasympathetic and sympathetic). This is a sign that your nervous system is balanced, and that your body is very capable of adapting to its environment and performing at its best.";
                  }
                } else
                  detailDescription =
                    "Not enought data to process. This happens when the signal strength of the scan is poor due to improper light or too much movements.";
                return (
                  <>
                    <div className="px-5 py-4 rounded-2xl bg-white border border-gray-100 shadow-sm">
                      <div className="flex items-center justify-between space-x-4">
                        {selectedRange.icon}
                        <div className="flex-grow">
                          <h2 className="text-sm font-semibold text-gray-800">Stress Level</h2>
                          <h4 className="mt-1 text-xs font-semibold uppercase" style={{ color: selectedRange.color }}>
                            {selectedRange.label}
                          </h4>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <h4 className="text-2xl font-bold text-gray-800">{stressLevel}</h4>
                          <h5 className="text-xs text-gray-400">{constants["Stress Level"].unit}</h5>
                        </div>
                      </div>
                      <div className="mt-4 pt-3 border-t border-gray-100">
                        <h5 className="text-xs font-semibold text-[#1845a3]">Your Stress Level is {selectedRange.label}</h5>
                        <p className="mt-1.5 text-xxs text-gray-500 leading-relaxed">{constants["Stress Level"].description}</p>
                      </div>
                    </div>
                    <div className="p-5 rounded-2xl bg-gradient-to-br from-[#1845a3]/5 to-[#ED1C24]/5 border border-[#1845a3]/10 space-y-2">
                      {detailTitle.length > 0 && <h3 className="text-sm font-semibold text-gray-800">{detailTitle}</h3>}
                      <p className="text-xxs text-gray-600 text-justify whitespace-pre-line leading-relaxed">{detailDescription}</p>
                      <h5 className="text-xs text-[#1845a3] text-center font-semibold pt-2">Autonomic Nervous Systems (ANS)</h5>
                    </div>
                  </>
                );
              })()}
            </Tab.Panel>
          )}
          <div className="p-5 rounded-2xl bg-white border border-gray-100 shadow-sm">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#1845a3] to-[#ED1C24] flex items-center justify-center">
                <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-800">Send Report via Email</h4>
                <p className="text-xxs text-gray-400">Enter email to receive the PDF report</p>
              </div>
            </div>
            {emailSent ? (
              <div className="flex items-center space-x-2 p-3 rounded-xl bg-green-50 border border-green-200">
                <svg className="w-5 h-5 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <span className="text-xs text-green-700 font-medium">Report sent successfully to {emailAddress}</span>
              </div>
            ) : (
              <>
                <div className="flex space-x-2">
                  <input
                    type="email"
                    value={emailAddress}
                    onChange={(e) => { setEmailAddress(e.target.value); setEmailError(""); }}
                    placeholder="Enter email address"
                    className="flex-grow px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#1845a3] focus:ring-1 focus:ring-[#1845a3]/20 transition-colors"
                  />
                  <button
                    type="button"
                    onClick={handleSendEmail}
                    disabled={emailSending}
                    className="shrink-0 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#1845a3] to-[#ED1C24] text-white text-xs font-semibold shadow-md disabled:opacity-50"
                  >
                    {emailSending ? "Sending..." : "Send"}
                  </button>
                </div>
                {emailError && <p className="mt-2 text-xxs text-red-500">{emailError}</p>}
              </>
            )}
            <button
              type="button"
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="w-full mt-3 py-2.5 rounded-xl border-2 border-[#1845a3] text-[#1845a3] text-xs font-semibold flex items-center justify-center space-x-2 disabled:opacity-50"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
              <span>{downloading ? "Preparing PDF..." : "Download PDF Report"}</span>
            </button>
          </div>
          {pdfSentOrDownloaded && !insuranceSubmitted && (
            <div className="p-5 rounded-2xl bg-white border border-gray-100 shadow-sm">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#1845a3] to-[#ED1C24] flex items-center justify-center">
                  <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-800">Insurance Questionnaire</h4>
                  <p className="text-xxs text-gray-400">(Optional) Help us understand your insurance needs</p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="p-3 rounded-xl bg-gray-50 border border-gray-100">
                  <p className="text-xs font-medium text-gray-700 mb-2">Do you currently have any Health Insurance policy?</p>
                  <div className="flex space-x-3">
                    {["Yes", "No"].map((opt) => (
                      <button key={opt} type="button" onClick={() => setInsuranceForm((p) => ({ ...p, hasHealthInsurance: opt === "Yes" }))} className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-colors ${insuranceForm.hasHealthInsurance === (opt === "Yes") ? "bg-gradient-to-r from-[#1845a3] to-[#ED1C24] text-white" : "bg-white border border-gray-200 text-gray-600"}`}>{opt}</button>
                    ))}
                  </div>
                </div>
                <div className="p-3 rounded-xl bg-gray-50 border border-gray-100">
                  <p className="text-xs font-medium text-gray-700 mb-2">Do you have any Motor Insurance?</p>
                  <div className="flex space-x-3">
                    {["Yes", "No"].map((opt) => (
                      <button key={opt} type="button" onClick={() => setInsuranceForm((p) => ({ ...p, hasMotorInsurance: opt === "Yes", motorType: opt === "No" ? "" : p.motorType }))} className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-colors ${insuranceForm.hasMotorInsurance === (opt === "Yes") ? "bg-gradient-to-r from-[#1845a3] to-[#ED1C24] text-white" : "bg-white border border-gray-200 text-gray-600"}`}>{opt}</button>
                    ))}
                  </div>
                  {insuranceForm.hasMotorInsurance && (
                    <div className="mt-3">
                      <p className="text-xxs text-gray-500 mb-2">Please specify type:</p>
                      <div className="flex flex-wrap gap-2">
                        {["Motor 2W", "Motor 4W", "Both"].map((type) => (
                          <button key={type} type="button" onClick={() => setInsuranceForm((p) => ({ ...p, motorType: type }))} className={`px-3 py-1.5 rounded-lg text-xxs font-semibold transition-colors ${insuranceForm.motorType === type ? "bg-gradient-to-r from-[#1845a3] to-[#ED1C24] text-white" : "bg-white border border-gray-200 text-gray-600"}`}>{type}</button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-3 rounded-xl bg-gray-50 border border-gray-100">
                  <p className="text-xs font-medium text-gray-700 mb-2">Do you have cover for Personal Accident and Critical Illness?</p>
                  <div className="flex space-x-3">
                    {["Yes", "No"].map((opt) => (
                      <button key={opt} type="button" onClick={() => setInsuranceForm((p) => ({ ...p, hasPaCiCover: opt === "Yes" }))} className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-colors ${insuranceForm.hasPaCiCover === (opt === "Yes") ? "bg-gradient-to-r from-[#1845a3] to-[#ED1C24] text-white" : "bg-white border border-gray-200 text-gray-600"}`}>{opt}</button>
                    ))}
                  </div>
                </div>
                <div className="p-3 rounded-xl bg-gray-50 border border-gray-100">
                  <p className="text-xs font-medium text-gray-700 mb-2">Do you have any active insurance cover for your owned House?</p>
                  <div className="flex space-x-3">
                    {["Yes", "No"].map((opt) => (
                      <button key={opt} type="button" onClick={() => setInsuranceForm((p) => ({ ...p, hasHouseInsurance: opt === "Yes" }))} className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-colors ${insuranceForm.hasHouseInsurance === (opt === "Yes") ? "bg-gradient-to-r from-[#1845a3] to-[#ED1C24] text-white" : "bg-white border border-gray-200 text-gray-600"}`}>{opt}</button>
                    ))}
                  </div>
                </div>
              </div>
              <button
                className="w-full mt-4 py-2.5 rounded-full bg-gradient-to-r from-[#1845a3] to-[#ED1C24] text-white text-xs font-semibold shadow-md disabled:opacity-50"
                type="button"
                onClick={handleInsuranceSubmit}
                disabled={insuranceSubmitting}
              >
                {insuranceSubmitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          )}
          {pdfSentOrDownloaded && insuranceSubmitted && (
            <div className="flex items-center space-x-2 p-4 rounded-2xl bg-green-50 border border-green-200">
              <svg className="w-5 h-5 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              <span className="text-xs text-green-700 font-medium">Insurance questionnaire submitted. Thank you!</span>
            </div>
          )}
          <button
            type="button"
            onClick={() =>
              navigate("/basic-details", {
                state: {
                  phoneNumber: result?.notes?.phone_number || "",
                  scanDuration,
                },
              })
            }
            className="w-full py-3.5 px-5 rounded-2xl bg-gradient-to-r from-[#1845a3] to-[#ED1C24] text-white text-xs font-semibold shadow-lg flex items-center justify-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
            <span>Scan More Family Members</span>
          </button>
          <div className="p-5 rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200">
            <div className="flex items-center space-x-2 mb-3">
              <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              <h4 className="text-xs font-semibold text-gray-600">Disclaimer</h4>
            </div>
            <p className="text-xxs text-gray-500 text-justify leading-relaxed">
              For Investigational Use Only. Careplix is not a substitute for the clinical judgment of a healthcare professional.
              Careplix is intended to improve your awareness of general wellness. Careplix does not diagnose, treat, mitigate or
              prevent any disease, symptom, disorder or abnormal physical state. Consult with a health care professional or
              emergency services if you believe you may have a medical issue.
            </p>
            <div className="mt-4 flex justify-center">
              <button
                className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#1845a3] to-[#ED1C24] text-white text-xs font-semibold shadow-md"
                type="button"
                onClick={() => setShowBackConfirm(true)}
              >
                {state?.fromHistory ? "Go Back" : "Back to Dashboard"}
              </button>
            </div>
          </div>
        </Tab.Panels>
      </Tab.Group>
      </div>
      <Transition show={modalState.visible} as={Fragment}>
        <Dialog as={Fragment} onClose={closeModal}>
          <Transition.Child
            className="fixed top-0 bottom-0 left-0 right-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50"
            enter="duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="duration-300 delay-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Transition.Child
              as={Fragment}
              enter="delay-150 duration-200"
              enterFrom="opacity-0 scale-50"
              enterTo="opacity-100 scale-100"
              leave="duration-100"
              leaveFrom="opacity-50 scale-50"
              leaveTo="opacity-0 scale-0"
            >
              <Dialog.Panel className="w-4/5 bg-white rounded-2xl overflow-hidden shadow-xl">
                <div className="h-1 bg-gradient-to-r from-[#1845a3] to-[#ED1C24]" />
                <div className="p-6 flex flex-col items-center text-center">
                  <Dialog.Title className="text-sm font-bold text-gray-800">{modalState.title}</Dialog.Title>
                  <Dialog.Description className="mt-3 text-xxs text-gray-500 leading-relaxed">{modalState.desc}</Dialog.Description>
                  <button
                    className="mt-5 px-6 py-2 rounded-full bg-gradient-to-r from-[#1845a3] to-[#ED1C24] text-white text-xs font-semibold shadow-md"
                    type="button"
                    onClick={closeModal}
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </Transition.Child>
        </Dialog>
      </Transition>
      <Transition show={showBackConfirm} as={Fragment}>
        <Dialog as={Fragment} onClose={() => setShowBackConfirm(false)}>
          <Transition.Child
            className="fixed top-0 bottom-0 left-0 right-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50"
            enter="duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="duration-300 delay-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Transition.Child
              as={Fragment}
              enter="delay-150 duration-200"
              enterFrom="opacity-0 scale-50"
              enterTo="opacity-100 scale-100"
              leave="duration-100"
              leaveFrom="opacity-50 scale-50"
              leaveTo="opacity-0 scale-0"
            >
              <Dialog.Panel className="w-4/5 bg-white rounded-2xl overflow-hidden shadow-xl">
                <div className="h-1 bg-gradient-to-r from-[#1845a3] to-[#ED1C24]" />
                <div className="p-6 flex flex-col items-center text-center">
                  <Dialog.Title className="text-sm font-bold text-gray-800">Do you want to go back?</Dialog.Title>
                  <Dialog.Description className="mt-2 text-xxs text-gray-500">You will leave the current scan results page.</Dialog.Description>
                  <div className="mt-5 flex space-x-3 w-full">
                    <button
                      className="flex-1 py-2.5 rounded-full border border-gray-200 text-gray-500 text-xs font-semibold"
                      type="button"
                      onClick={() => setShowBackConfirm(false)}
                    >
                      No
                    </button>
                    <button
                      className="flex-1 py-2.5 rounded-full bg-gradient-to-r from-[#1845a3] to-[#ED1C24] text-white text-xs font-semibold shadow-md"
                      type="button"
                      onClick={() => {
                        setShowBackConfirm(false);
                        navigate(state?.fromHistory ? -1 : "/");
                      }}
                    >
                      Yes
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </Transition.Child>
        </Dialog>
      </Transition>
    </section>
  ) : (
    <Navigate to="/" replace />
  );
};

export default ScanResult;
